import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Divider,
  Chip,
  useTheme,
  CustomTheme,
} from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { StandardDialog } from 'shared-components/components';
import { FetchPlansResponse, Plan } from '../Interface';
import dayjs from 'op-utils/dayjs';

interface PlanSelectionProps {
  open: boolean;
  onClose: () => void;
  handleSubmit: (selectedPlans: Plan[], noUnapprovalRequired: boolean) => void;
  fetchPlansResponse: FetchPlansResponse | null;
  isLoadingFetchPlans: boolean;
  onExited?: () => void;
  isSubmitting?: boolean;
}

const PlanSelection: React.FC<PlanSelectionProps> = ({
  open,
  onClose,
  handleSubmit,
  fetchPlansResponse,
  isLoadingFetchPlans,
  onExited,
  isSubmitting = false,
}) => {
  const [selectedPlans, setSelectedPlans] = useState<Plan[]>([]);
  const [noUnapprovalRequired, setNoUnapprovalRequired] = useState(false);
  const [sortBy, setSortBy] = useState<'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const theme = useTheme<CustomTheme>();

  const TOOLTIP_TEXT =
    'Submission requires either selecting impacted plans or confirming no treatment unapproval required.';
  const TREATMENT_APPROVED = 'Treatment approved';
  const PLANNING_APPROVED = 'Planning approved';

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedPlans([]);
      setNoUnapprovalRequired(false);
    }
  }, [open]);

  // Helper function to check if a plan is selected
  const isPlanSelected = (plan: Plan) => {
    return selectedPlans.some(
      (selectedPlan) => selectedPlan.plan_id === plan.plan_id && selectedPlan.course_id === plan.course_id,
    );
  };

  const handlePlanSelection = (plan: Plan) => {
    setSelectedPlans((prev) => {
      const isSelected = isPlanSelected(plan);

      if (isSelected) {
        // Remove the plan
        const newSelectedPlans = prev.filter(
          (selectedPlan) => !(selectedPlan.plan_id === plan.plan_id && selectedPlan.course_id === plan.course_id),
        );

        return newSelectedPlans;
      } else {
        // Add the plan
        const newSelectedPlans = [...prev, plan];

        // If any plan is selected, uncheck "No treatment unapproval required"
        if (newSelectedPlans.length > 0) {
          setNoUnapprovalRequired(false);
        }

        return newSelectedPlans;
      }
    });
  };

  const handleNoUnapprovalChange = (checked: boolean) => {
    setNoUnapprovalRequired(checked);
    if (checked) {
      setSelectedPlans([]);
    }
  };

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';

    try {
      const date = dayjs(dateString);
      if (!date.isValid()) return '-';

      return date.format('MMM-DD-YYYY, hh:mm A');
    } catch {
      return '-';
    }
  };

  const sortPlans = (plans: Plan[]) => {
    return [...plans].sort((a, b) => {
      const aValue = dayjs(a.created_at).valueOf();
      const bValue = dayjs(b.created_at).valueOf();

      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  const renderPlansContent = () => {
    // Show error if there's an error message or if the request was unsuccessful
    if (fetchPlansResponse?.errorMessage || !fetchPlansResponse?.success) {
      return (
        <Box sx={{ p: 1, backgroundColor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
            {fetchPlansResponse?.errorMessage || 'Failed to fetch plans'}
          </Typography>
        </Box>
      );
    }

    // Show plans if they exist
    if (fetchPlansResponse && fetchPlansResponse.iguanaResponse?.plans?.length > 0) {
      const plans = fetchPlansResponse.iguanaResponse.plans;

      // Separate plans by status
      const treatmentApprovedPlans = plans.filter((plan: Plan) => plan.plan_status === TREATMENT_APPROVED);
      const planningApprovedPlans = plans.filter((plan: Plan) => plan.plan_status === PLANNING_APPROVED);

      // Apply sorting to all plans together, then separate them again
      const allPlans = [...plans];
      const sortedAllPlans = sortPlans(allPlans);
      const sortedTreatmentApprovedPlans = sortedAllPlans.filter(
        (plan: Plan) => plan.plan_status === TREATMENT_APPROVED,
      );

      const handleSelectAll = (checked: boolean) => {
        if (checked) {
          // Select all treatment approved plans
          setSelectedPlans(sortedTreatmentApprovedPlans);
          setNoUnapprovalRequired(false);
        } else {
          // Deselect all plans
          setSelectedPlans([]);
        }
      };

      const isAllSelected =
        sortedTreatmentApprovedPlans.length > 0 && selectedPlans.length === sortedTreatmentApprovedPlans.length;

      return (
        <Box component={Paper} elevation={0}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selectedPlans.length > 0 && selectedPlans.length < sortedTreatmentApprovedPlans.length
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plans</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '90px' }}>Course</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Latest treatment date</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={handleSort}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {sortOrder === 'asc' ? (
                        <ArrowUpward sx={{ color: theme.palette.action.active }} />
                      ) : (
                        <ArrowDownward sx={{ color: theme.palette.action.active }} />
                      )}
                      <span style={{ marginLeft: '8px' }}>Created date</span>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Treatment Approved Plans */}
                {sortedTreatmentApprovedPlans.map((plan: Plan, index: number) => (
                  <TableRow key={index} hover onClick={() => handlePlanSelection(plan)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isPlanSelected(plan)}
                        onChange={() => handlePlanSelection(plan)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>{plan.plan_id}</TableCell>
                    <TableCell sx={{ width: '90px' }}>{plan.course_id}</TableCell>
                    <TableCell>{plan.plan_status}</TableCell>
                    <TableCell>{formatDate(plan.latest_treatment)}</TableCell>
                    <TableCell>{formatDate(plan.created_at)}</TableCell>
                  </TableRow>
                ))}

                {/* "Other Plans" Divider Row */}
                {planningApprovedPlans.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            px: 2,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                          }}>
                          OTHER PLANS
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Planning Approved Plans */}
                {planningApprovedPlans.map((plan: Plan, index: number) => (
                  <TableRow key={`planning-${index}`} hover sx={{ '&:first-of-type td': { borderTop: 'none' } }}>
                    <TableCell sx={{ color: 'text.secondary' }} colSpan={2}>
                      {plan.plan_id}
                    </TableCell>
                    <TableCell sx={{ width: '90px', color: 'text.secondary' }}>{plan.course_id}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{plan.plan_status}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatDate(plan.latest_treatment)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatDate(plan.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        No plans available
      </Typography>
    );
  };

  const isDisabled = isLoadingFetchPlans || (selectedPlans.length === 0 && !noUnapprovalRequired) || isSubmitting;
  const tooltipText = isDisabled ? TOOLTIP_TEXT : undefined;

  return (
    <StandardDialog
      title="Impacted RT plans detected"
      maxWidth="lg"
      open={open}
      onClose={onClose}
      submitText="SUBMIT CHANGES"
      submitButtonProps={{
        disabled: isDisabled,
        startIcon: isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined,
      }}
      submitButtonTooltip={tooltipText}
      closeText="CANCEL"
      onSubmit={() => {
        handleSubmit(selectedPlans, noUnapprovalRequired);
      }}
      TransitionProps={{
        onExited: onExited,
      }}>
      <Typography paddingY={2}>
        Select any impacted plans to immediately <strong>remove "treatment approval"</strong> to prevent treatment from
        proceeding under outdated prescription details. If none of the plans listed are impacted you may continue
        without selecting any plans.
      </Typography>

      <Alert severity="warning" sx={{ border: '1px solid var(--warning-main, #FFBD5B)' }}>
        Submitting this change is a clinical decision. It is your responsibility to determine which RT plans, if any,
        should be reverted and to notify the appropriate team to update the plans accordingly.
      </Alert>

      {(fetchPlansResponse || isLoadingFetchPlans) && (
        <Box mt={2}>
          {isLoadingFetchPlans ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={3}>
              <CircularProgress size={48} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Retrieving plans from TPS...
              </Typography>
            </Box>
          ) : (
            <>
              {renderPlansContent()}

              {/* Selected Plans Section */}
              {selectedPlans.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Selected:
                      </Typography>
                      {selectedPlans.map((plan) => {
                        return (
                          <Chip
                            key={`${plan.plan_id}-${plan.course_id}`}
                            label={plan.plan_id}
                            onDelete={() => handlePlanSelection(plan)}
                            color="primary"
                            variant="filled"
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ border: '1px solid grey', backgroundColor: theme.palette.grey[200], borderRadius: 1, p: 1 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  onClick={() => handleNoUnapprovalChange(!noUnapprovalRequired)}
                  sx={{ cursor: 'pointer' }}>
                  <Checkbox
                    checked={noUnapprovalRequired}
                    onChange={(e) => handleNoUnapprovalChange(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography variant="body1">
                    <Typography component="span" variant="subtitle1">
                      No treatment unapproval required
                    </Typography>
                    {' - continue without selecting any plans.'}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </StandardDialog>
  );
};

export default PlanSelection;
